import {
  Component,
  Input,
  Type,
  TemplateRef,
  Output,
  EventEmitter,
  ViewChild,
  ContentChild,
  Injector,
  Optional,
  ViewContainerRef,
  ChangeDetectorRef,
  Renderer2,
  Inject,
  AfterViewInit,
} from "@angular/core";
import { NzDrawerComponent, NzDrawerRef } from "ng-zorro-antd/drawer";
import { NzConfigService, WithConfig } from "ng-zorro-antd/core/config";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { NgStyleInterface, NzSafeAny } from "ng-zorro-antd/core/types";
import { DrawerRef } from "@webapp/ui/drawer/abstracts/drawer-ref";
import { DrawerPlacement } from "@webapp/ui/drawer/drawer.models";
import { DrawerContentDirective } from "@webapp/ui/drawer/directives/drawer-content.directive";
import { CdkPortalOutlet, ComponentPortal, TemplatePortal } from "@angular/cdk/portal";
import { Directionality } from "@angular/cdk/bidi";
import { Overlay, OverlayKeyboardDispatcher, OverlayRef } from "@angular/cdk/overlay";
import { FocusTrap, FocusTrapFactory } from "@angular/cdk/a11y";
import { DOCUMENT } from "@webapp/core/factories/document.factory";
@Component({
  selector: "ui-drawer",
  exportAs: "uiDrawer",
  templateUrl: "./drawer.component.html",
})
export class UiDrawerComponent<T = NzSafeAny, R = NzSafeAny, D = NzSafeAny> extends NzDrawerComponent implements AfterViewInit {
  @Input("uiContent") public nzContent!: TemplateRef<{ $implicit: D; drawerRef: DrawerRef<R> }> | Type<T>;
  @Input("uiCloseIcon") public nzCloseIcon: string | TemplateRef<void> = "close";
  @Input("uiClosable") @InputBoolean() public nzClosable = true;
  @Input("uiMaskClosable") @WithConfig() @InputBoolean() public nzMaskClosable = true;
  @Input("uiMask") @WithConfig() @InputBoolean() public nzMask = true;
  @Input("uiCloseOnNavigation") @WithConfig() @InputBoolean() public nzCloseOnNavigation = true;
  @Input("uiNoAnimation") @InputBoolean() public nzNoAnimation = false;
  @Input("uiKeyboard") @InputBoolean() public nzKeyboard = true;
  @Input("uiTitle") public nzTitle?: string | TemplateRef<unknown>;
  @Input("uiFooter") public nzFooter?: string | TemplateRef<unknown>;
  @Input("uiPlacement") public nzPlacement: DrawerPlacement = "right";
  @Input("uiMaskStyle") public nzMaskStyle: NgStyleInterface = {};
  @Input("uiBodyStyle") public nzBodyStyle: NgStyleInterface = {};
  @Input("uiWrapClassName") public nzWrapClassName?: string;
  @Input("uiWidth") public nzWidth: number | string = 256;
  @Input("uiHeight") public nzHeight: number | string = 256;
  @Input("uiZIndex") public nzZIndex = 1000;
  @Input("uiOffsetX") public nzOffsetX = 0;
  @Input("uiOffsetY") public nzOffsetY = 0;

  @Input("uiVisible")
  public set uiVisible(value: boolean) {
    super.nzVisible = value;
  }

  public get uiVisible(): boolean {
    return this.isOpen;
  }

  @Output("uiOnViewInit") public readonly nzOnViewInit = new EventEmitter<void>();
  @Output("uiOnClose") public readonly nzOnClose = new EventEmitter<MouseEvent>();
  @Output("uiVisibleChange") public readonly nzVisibleChange = new EventEmitter<boolean>();

  @ViewChild("drawerTemplate", { static: true }) public drawerTemplate!: TemplateRef<void>;
  @ViewChild(CdkPortalOutlet, { static: false }) public portalOutlet?: CdkPortalOutlet;
  @ContentChild(DrawerContentDirective, { static: true, read: TemplateRef })
  public contentFromContentChild?: TemplateRef<NzSafeAny>;

  private uiComponentInstance: T | null = null;

  public previouslyFocusedElement?: HTMLElement;
  public placementChanging = false;
  public placementChangeTimeoutId = -1;
  public nzContentParams?: D; // only service
  public overlayRef?: OverlayRef | null;
  public portal?: TemplatePortal;
  public focusTrap?: FocusTrap;
  public isOpen = false;
  public inAnimation = false;
  public templateContext: { $implicit: D | undefined; drawerRef: NzDrawerRef<R> } = {
    $implicit: undefined,
    drawerRef: this as unknown as NzDrawerRef<R>,
  };

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  public constructor(
    uiCdr: ChangeDetectorRef,
    @Optional() @Inject(DOCUMENT) uiDocument: NzSafeAny,
    uiConfigService: NzConfigService,
    uiRenderer: Renderer2,
    uiOverlay: Overlay,
    private uiInjector: Injector,
    uiChangeDetectorRef: ChangeDetectorRef,
    uiFocusTrapFactory: FocusTrapFactory,
    uiViewContainerRef: ViewContainerRef,
    uiOverlayKeyboardDispatcher: OverlayKeyboardDispatcher,
    @Optional() uiDirectionality: Directionality
  ) {
    super(
      uiCdr,
      uiDocument,
      uiConfigService,
      uiRenderer,
      uiOverlay,
      uiInjector,
      uiChangeDetectorRef,
      uiFocusTrapFactory,
      uiViewContainerRef,
      uiOverlayKeyboardDispatcher,
      uiDirectionality
    );
  }

  public ngAfterViewInit(): void {
    // do not invoke the parent ngAfterViewInit
    this.uiAttachBodyContent();
    if (this.nzOnViewInit.observers.length) {
      setTimeout(() => {
        this.nzOnViewInit.emit();
      });
    }
  }

  public close(result?: R): void {
    this.uiComponentInstance = null;
    super.close(result);
  }

  public getContentComponent(): T | null {
    return this.uiComponentInstance;
  }

  private uiAttachBodyContent(): void {
    this.bodyPortalOutlet!.dispose();

    if (this.nzContent instanceof Type) {
      const childInjector = Injector.create({
        parent: this.uiInjector,
        providers: [{ provide: DrawerRef, useValue: this }],
      });
      const componentPortal = new ComponentPortal<T>(this.nzContent, null, childInjector);
      const componentRef = this.bodyPortalOutlet!.attachComponentPortal(componentPortal);
      this.uiComponentInstance = componentRef.instance;
      Object.assign(componentRef.instance, this.nzContentParams);
      componentRef.changeDetectorRef.detectChanges();
    }
  }
}
